<template>
  <div v-if="surveyInitialized" class="row justify-content-center">
    <loader :loading="loading"></loader>
    <div class="col-md-6">
      <survey-header
        :title="surveyName"
        :displayOptions="getDisplayOptionsByType(displayTypes.HEADER)">
      </survey-header>
      <survey-sections 
        :missingResponseSection="firstNoResponseSection"
        :invalidate="invalidate"
        @putSectionResponses="putSectionResponses"
        @nav="handleNav">
      </survey-sections>
    </div>
  </div>
</template>
<script>
  import Loader from './Loader';
  import SurveyHeader from './SurveyHeader';
  import SurveySections from './Sections';
  import { mapGetters, mapActions } from 'vuex';
  import * as surveyActions from '../../modules/surveyActions';
  import { isNullOrEmpty } from 'src/modules/utils';

  export default {
    components: {
      Loader,
      SurveyHeader,
      SurveySections
    },
    data() {
      return {
        sectionRouteName: 'ActiveSurveySection',
        invalidate: false,
        firstMissingResponseQid: null,
      }
    },
    computed: {
      surveyName() {
        return this.activeSurvey ? this.activeSurvey.surveyName : '';
      },
      currentSectionId: {
        get() {
          return this.activeSectionId;
        },
        set(value) {
          this.setActiveSectionId(Number(value));
        }
      },
      nextSectionId() {
        return this.getNextSectionId;
      },
      loading() {
        return !this.surveyInitialized;
      },
      // Define computed prop hasResponseTriggers to cache the getter surveyHasResponseTriggers().
      hasResponseTriggers() {
        return this.surveyHasResponseTriggers;
      },
      requiredQuestions() {
        return this.activeSurveyRequiredQuestions;
      },
      allResponses() {
        return this.getAllQuestionResponses;
      },
      respondedToQuestionIds() {
        let requiredQs = this.requiredQuestions;
        let responses = this.allResponses;

        // Get just one response to each question of interest because each question should have 
        // at least one response entry (checkboxes might have more than one),
        // so find distinct ID's (HspCategoryQuestionId) from the response list.
        let respondedToQs = requiredQs.flat(Infinity).filter(x => responses.find(y => x.hspCategoryQuestionId === y.hspCategoryQuestionId));
        const qIds = respondedToQs.map(x => x.hspCategoryQuestionId);

        return qIds;
      },
      isCompleted() {
        let requiredQs = this.requiredQuestions;
        let respondedToQids = this.respondedToQuestionIds;
        let isCompleted = respondedToQids.length < requiredQs.length ? false : true;

        return isCompleted;
      },
      completenessCheckType() {
        return !isNullOrEmpty(process.env.VUE_APP_COMPLETENESS_CHECK) ? process.env.VUE_APP_COMPLETENESS_CHECK : this.dccTypes.END_OF_SURVEY;
      },
      firstQSectionIndex() {
        return this.firstQuestionNavSectionIndex;
      },
      dccTypes() {
        return this.dataCompletenessCheckTypes;
      },
      validateData() {
        // Enable validation if this property was set from the server response.
        if (this.firstMissingResponseQid) {
          return true;
        }

        // No data completeness check
        if (this.completenessCheckType === this.dccTypes.NONE) {
          return false;
        }

        // Validate data completeness at the end of the survey.
        if (this.completenessCheckType === this.dccTypes.END_OF_SURVEY && (this.surveyUser.userSurveyStatusId > 3 || !this.isLastQsSection)) {
          return false;
        }

        const currentSectionIndex = this.getNavSectionIndexById(this.currentSectionId);
        if (this.firstQSectionIndex < 0 || currentSectionIndex < this.firstQSectionIndex) {
          return false;
        }

        return true;
      },
      // get/set the data field firstMissingResponseQid as the primary value for storing the first missing response question ID that's
      // 'manually' set when the server-side check for data completeness fails. Otherwise, the first no response question ID is
      // based on local storage of the user responses and the required number of questions (dependent on branching and how the 
      // user responds to the branches).
      firstNoResponseQid: {
        get() {
          if (this.firstMissingResponseQid) {
            return this.firstMissingResponseQid;
          }

          return this.findFirstNoResponseQid();
        },
        set(value) {
          this.firstMissingResponseQid = value;
        }
      },
      firstNoResponseSection() {
        if (!this.validateData) {
          return null;
        }

        return this.firstNoResponseQid ? this.getSectionByQuestionId(this.firstNoResponseQid) : null;
      },
      // More specific and readable usage of firstNoResponseSection.
      hasMissingResponses() {
        return this.firstNoResponseSection ? true : false;
      },
      isLastQsSection() {
        return this.isLastQuestionsSection(this.hasResponseTriggers);
      },
      ...mapGetters([
        'surveyInitialized',
        'surveyUser',
        'activeSurvey',
        'activeSectionId',
        'isLastQuestionsSection',
        'getNavSectionById',
        'getNavSectionIndexById',
        'getRequiredSectionById',
        'displayTypes',
        'getDisplayOptionsByType',
        'activeSurveySections',
        'getSectionByQuestionId',
        'activeSurveyRequiredQuestions',
        'getAllQuestionResponses',
        'getNextSectionId',
        'surveyHasResponseTriggers',
        'firstQuestionSectionIndex',
        'firstQuestionNavSectionIndex',
        'dataCompletenessCheckTypes',
      ])
    },
    methods: {
      async handleNav(type, categoryQuestionIds, passed) {
        if(type === 'previous') {
          this.setPreviousSection();
          this.triggerNav(type);
        }
        else if(type === 'next') {
          if(passed === true) {
            // NOTE: First-level survey data completeness check.
            // The property 'this.firstNoResponseSection' checks for missing reponses when the survey reaches the last section with questions.
            let navNormal = true;
            let completedMsg = null;
            if (!this.hasMissingResponses) {
              // No missing responses. Continue (navigate 'next') as normal.
              completedMsg = await this.setNextSection();
            }
            // Not completed, take the user to the section of the missing response(s).
            else {
              navNormal = false;
              this.setMissingResponseSection();
            }

            // PUT user responses on next navigation
            // Await on the last survey page so the server will have the most updated user responses to check for data completeness of 'responded-to" required questions.
            const isAsync = !isNullOrEmpty(completedMsg) || (this.isLastQsSection && this.surveyUser.userSurveyStatusId < 4) ? true : false;
            await this.putSectionResponses(categoryQuestionIds, isAsync);

            // We can call 'await this.triggerNav(type)' since this is an async function, but no need since we're not
            // doing anything after the call that needs an await.
            this.triggerNav(type, navNormal, completedMsg);
          }
        }
      },
      async triggerNav(type, navNormal = true, completedMsg = null) {
        this.$emit('nav', type);
        surveyActions.logSectionAccess();
        let routeName = null;
        let params = {};
        const hasCompletedMsg = !isNullOrEmpty(completedMsg);

        let statusVal = 3;
        if (hasCompletedMsg || (navNormal && this.atEndOfSurvey(this.currentSectionId))) {
          // Set survey status to completed if last page has no questions
          // in case user clicks away from survey before clicking 'Finish'
          // OR if user clicks on Finish.
          statusVal = 4;
        }
        const isAsync = statusVal === 4;
        let success = false;
        if (!isAsync) {
          success = this.setStatus(statusVal);
        }
        else {
          success = await this.setStatusAsync(statusVal);
        }

        if (this.currentSectionId === null || hasCompletedMsg) {
          routeName = 'Info';
          this.setSurveyInitialized(false);
          params.type = 'completed';
          params.surveyId = this.$route.params.surveyId;
          if (hasCompletedMsg) {
            params.completedMsg = completedMsg;
          }
        }
        else {
          routeName = this.sectionRouteName;
          params = Object.assign(params, this.$route.params, {sectionId: this.currentSectionId});
        }

        this.$router.push({name: routeName, params: params});
      },
      setStatus(statusVal) {
        this.setUserStatus({ status: statusVal });
        return true;
      },
      async setStatusAsync(statusVal) {
        let result = await this.setUserStatusAsync({ status: statusVal });

        // NOTE: Second-level survey data completeness check.
        // Second level missing response(s) check for survey data coming from the server-side check.
        if (result && result.error && result.error.length > 0) {
          if (result.error === "MissingResponse") {
            // NOTE: Server-side survey data completeness check.
            // By this point, the server has checked and found missing responses. The call to findFirstNoResponseQid() should return the first missing response ID
            // so it can be used (to set the computed prop firstNoResponseQid) to trigger navigation back to the section of the missing response(s). 
            // If this returns a 'null' (no missing response/data), then there's a discrepancy between the response data returned from the server 
            // and the server telling the client that there is missing response(s).
            // EXAMPLE: For this scenario, you might TEST whether the missing response(s) check works on both the server and client side by setting
            //          the UserSurveyStatusId back to 3 in the server API even though the user has completed the survey without missing responses
            //          (the server API has detected a valid UserSurveyStatusId=4). Then findFirstNoResponseQid() will return null, and the user
            //          will be navigated to the 'Thank you' end page. This example requires developer intervention to manipulate the data on the
            //          server side and should not happen in a normal survey taking environment; and if it does, someone has hijacked the API.
            const qids = result.responses.map(x => x.hspCategoryQuestionId);
            const missingResponseQid = this.findFirstNoResponseQid(qids);
            const hasMissingResponses = missingResponseQid ? true : false;
            if (hasMissingResponses) {
              this.firstNoResponseQid = missingResponseQid;
              // Automatically jump to the missing response section if about to navigate out of the survey.
              if (!this.activeSectionId) {
                const section = this.findFirstNoResponseSection(missingResponseQid);
                this.setMissingResponseSection(section);
              }
            }

            // If hasMissingResponses (navigated to section of missing response(s)) is true, return false, 
            // i.e., return the opposite of the value returned from setMissingResponseSection().
            return !hasMissingResponses;
          }
        }

        return true;
      },
      // If there are missing responses, navigates to the first section of the missing response(s) and returns true; else returns false.
      setMissingResponseSection(section = this.firstNoResponseSection) {
        if (section) {
          this.setSection({
            triggerSectionId: this.currentSectionId,
            targetSectionId: section.hspSectionId
          });
          this.invalidate = true;

          // Reset the the property (that was manually set during failed server-side data completion check) after jumping to the 
          // missing response(s) section so we don't keep jumping to the same section (infinite loop).
          this.firstNoResponseQid = null;

          return true;
        }

        return false;
      },
      findFirstNoResponseQid(respondedToQids = this.respondedToQuestionIds) {
        let requiredQs = this.requiredQuestions;

        // Find the first question that has no reponse
        let qId = null;
        for (var i = 0; i < requiredQs.length; i++) {
          let found = respondedToQids.find(x => requiredQs[i].hspCategoryQuestionId === x);
          if (!found) {
            qId = requiredQs[i].hspCategoryQuestionId;
            break;
          }
        }

        qId = typeof qId === "undefined" ? null : qId;

        return qId;
      },
      findFirstNoResponseSection(qid) {
        return qid ? this.getSectionByQuestionId(qid) : null;
      },
      atEndOfSurvey(sectionId) {
        let isEnd = false;
        if (sectionId === null) {
          isEnd = true;
        }
        else {
          let section = this.getRequiredSectionById(sectionId);
          isEnd = !section && this.isLastQsSection;
        }

        return isEnd;
      },
      async putSectionResponses(categoryQuestionIds, isAsync = true) {
        if (!isAsync) {
          surveyActions.putSectionResponses(categoryQuestionIds);
        }
        else {
          await surveyActions.putSectionResponses(categoryQuestionIds);
        }
      },
      // Check for survey data completeness. The check is made if we're at the end of the survey. Otherwise, this method returns true so processing can continue.
      async checkForCompletion(sectionId) {
        if (this.surveyUser.userSurveyStatusId > 3) {
          return true;
        }

        let success = true;
        let isEnd = typeof sectionId === "undefined" ? this.isLastQsSection : this.atEndOfSurvey(sectionId);
        if (isEnd) {
          // Need to perform an await to let the retrieved user data get propogated/mutated into the store 
          // before comparing the retrieved/mutated user survey data for completeness in the computed prop isCompleted().
          let data = await this.getSurveyUser({
            surveyUrlId: this.$route.params.surveyId
          });

          success = this.isCompleted;
        }

        return success;
      },
      ...mapActions([
        'updateSurveyUser',
        'getSurveyUser',
        'addActiveSurvey',
        'setActiveSectionId',
        'setUserStatus',
        'setUserStatusAsync',
        'setSurveyInitialized',
        'setPreviousSection',
        'setNextSection',
        'setSection',
      ])
    },
    created() {
      let payload = {
        surveyUrlId: this.$route.params.surveyId
      }

      // The call to this.updateSurveyUser() (which mutates the client's user responses retrieved from the server)
      // will trigger the computed property firstNoResponseSection to be updated, and thereby,
      // determining whether to display the 'missing responses' message in the active section.
      this.addActiveSurvey(payload)
        .then(() => this.updateSurveyUser({
            surveyUrlId: this.activeSurvey.surveyUrlId,
            userSurveyStatusId: 2
          })
        )
        .then(() => {
          if(this.surveyUser.userSurveyStatusId == 4) {
            throw 'completed';
          }
          else {
            let sectionId = surveyActions.getNavigationSectionId(this.$route.params.sectionId);
            this.currentSectionId = sectionId;

            if(this.$route.name !== this.sectionRouteName || +this.$route.params.sectionId !== +sectionId) {
              this.$router.replace({ name: this.sectionRouteName, params: Object.assign({}, this.$route.params, {sectionId: sectionId}) });
            }

            this.setSurveyInitialized(true);
            surveyActions.logSectionAccess();
          }
        })
        .catch(surveyActions.handleUnauthorizedError);
    },
    beforeRouteUpdate(to, from, next) {
      let sectionId = to.params.sectionId;
      let section = this.$store.getters.getNavSectionById(+sectionId);

      if(section && section.viewed === true) {
        this.currentSectionId = section.id;
        next();
      }
      else {
        next(false);
      }
    },
    beforeRouteLeave(to, from, next) {
      this.setActiveSectionId(null);
      next();
    }
  }
</script>
